import { useTranslation } from "react-i18next";

import type { VatDisplayMode } from "@/lib/vatDisplay";

/**
 * Segmentovaný pill toggle „S DPH | Bez DPH".
 *
 * Renderuje se jen pro B2B customers (caller decides via `visible`
 * prop). Pro B2C / anonymous se nezobrazuje (CZ zákon o ochraně
 * spotřebitele požaduje gross display jako finální cena).
 *
 * Žádný self-contained state — caller drží mode + onChange. Persistence
 * v localStorage je úkolem volajícího (přes ``writeVatDisplayPreference``).
 */
export function VatDisplayToggle({
  mode,
  onChange,
  visible = true,
  className = "",
}: {
  mode: VatDisplayMode;
  onChange: (mode: VatDisplayMode) => void;
  visible?: boolean;
  className?: string;
}): JSX.Element | null {
  const { t } = useTranslation("checkout");
  if (!visible) return null;
  return (
    <div
      className={`inline-flex rounded border border-border bg-bg text-xs ${className}`}
      role="group"
      aria-label={t("vatToggle.label", { defaultValue: "Zobrazení cen" })}
      data-testid="vat-display-toggle"
    >
      <button
        type="button"
        onClick={() => onChange("gross")}
        className={`rounded-l px-2 py-1 ${
          mode === "gross" ? "bg-ink text-bg" : "text-ink-3 hover:bg-surface-2"
        }`}
        aria-pressed={mode === "gross"}
        data-testid="vat-display-gross"
      >
        {t("vatToggle.gross", { defaultValue: "S DPH" })}
      </button>
      <button
        type="button"
        onClick={() => onChange("net")}
        className={`rounded-r px-2 py-1 ${
          mode === "net" ? "bg-ink text-bg" : "text-ink-3 hover:bg-surface-2"
        }`}
        aria-pressed={mode === "net"}
        data-testid="vat-display-net"
      >
        {t("vatToggle.net", { defaultValue: "Bez DPH" })}
      </button>
    </div>
  );
}
